
import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader2, CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { ExpenseRecordCreate } from '@/app/types/expense'; // Assuming this type matches the required backend structure
import { SimpleAccountSelector } from '@/app/components/common/SimpleAccountSelector';
import { Site } from '@/app/types/site';
import { usePreferenceStore } from '@/app/store/preferenceStore';
import { useAccountFilters } from '@/app/hooks/useAccountFilters';

// --- Corrected Account Type IDs (based on provided account_types.json) ---
// Note: Assuming ID 1 for Assets (Cash/Bank), ID 4 for Expenses.
//       Adjust if your specific Cash/Bank accounts have a dedicated type/group.
const ACCOUNT_TYPES = {
    ASSET: 1,     // For Cash, Bank Accounts etc. [cite: 1]
    LIABILITY: 2, // [cite: 1]
    INCOME: 3,    // [cite: 1]
    EXPENSE: 4,   // For Direct/Indirect Expenses [cite: 1]
    EQUITY: 7,    // [cite: 1]
    // Add others if needed, ensuring IDs match your database
};

// --- Zod Schema for Validation ---
const expenseBaseSchema = z.object({
  transaction_date: z.date({ required_error: 'Date is required' }),
  narration: z.string().optional().nullable(),
  site_id: z.number({ required_error: 'Site is required' }).int().positive('Site is required'),
});

const directSchema = expenseBaseSchema.extend({
  expense_type: z.literal('Direct'),
  // Use number().int().positive() for IDs if they must be positive integers
  paid_from_account_id: z.number({ required_error: 'Paid From account is required' }).int().positive('Account ID must be positive'),
  direct_expense_account_id: z.number({ required_error: 'Expense account is required' }).int().positive('Account ID must be positive'),
  // Use number().gt(0) for amount validation
  direct_amount: z.number({ required_error: 'Amount is required' }).gt(0, { message: 'Amount must be greater than 0' }),
  reference_number: z.string().optional().nullable(),
});

const adjustmentSchema = expenseBaseSchema.extend({
  expense_type: z.literal('Adjustment'),
  paid_from_account_id: z.number({ required_error: 'Paid From account is required' }).int().positive('Account ID must be positive'),
  paid_to_account_id: z.number({ required_error: 'Paid To account is required' }).int().positive('Account ID must be positive'),
  adjustment_amount: z.number({ required_error: 'Amount is required' }).gt(0, { message: 'Amount must be greater than 0' }),
  reference_number: z.string().optional().nullable(),
});

// Discriminated union based on expense_type
const expenseFormSchema = z.discriminatedUnion("expense_type", [
  directSchema,
  adjustmentSchema
]);

type ExpenseFormValues = z.infer<typeof expenseFormSchema>;

// --- Component Props ---
interface ExpenseFormProps {
  expense?: ExpenseFormValues; // For edit mode (if implemented later)
  isSubmitting: boolean;
  onSubmit: (data: ExpenseRecordCreate) => void; // Ensure ExpenseRecordCreate aligns with backend needs
  sites: Site[]; // Available sites for selection
}

export const ExpenseForm: React.FC<ExpenseFormProps> = ({
  expense,
  isSubmitting,
  onSubmit,
  sites,
}) => {
  const isEditMode = !!expense?.expense_type; // Basic check for edit mode
  
  // Get user preferences
  const { defaultSiteId } = usePreferenceStore();
  
  // Get account filters based on user's default site
  const { expenseAccountIds } = useAccountFilters();

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    // Set default values carefully, especially for potentially undefined numbers in Zod
    defaultValues: {
      expense_type: expense?.expense_type ?? 'Direct',
      transaction_date: expense?.transaction_date ?? new Date(),
      narration: expense?.narration ?? '',
      // Use preference site if available
      site_id: expense?.site_id ?? (defaultSiteId && sites.some(s => s.id === defaultSiteId) ? defaultSiteId : undefined),
      // Direct defaults/values
      paid_from_account_id: expense?.expense_type === 'Direct' ? expense.paid_from_account_id : undefined,
      direct_expense_account_id: expense?.expense_type === 'Direct' ? expense.direct_expense_account_id : undefined,
      direct_amount: expense?.expense_type === 'Direct' ? expense.direct_amount : undefined,
      // Adjustment defaults/values
      paid_to_account_id: expense?.expense_type === 'Adjustment' ? expense.paid_to_account_id : undefined,
      adjustment_amount: expense?.expense_type === 'Adjustment' ? expense.adjustment_amount : undefined,
      // Common reference number
      reference_number: expense?.reference_number ?? '',
    }
  });

  const expenseType = form.watch('expense_type');

  // Form submission handler
  const handleFormSubmit: SubmitHandler<ExpenseFormValues> = (data) => {
    // Format dates and ensure correct structure before calling onSubmit
    // Convert amounts back to numbers explicitly if needed, though Zod should handle this
    const formattedData: ExpenseRecordCreate = {
        transaction_date: format(data.transaction_date, 'yyyy-MM-dd'),
        narration: data.narration || null,
        reference_number: data.reference_number || null, // Common reference
        expense_type: data.expense_type,
        site_id: data.site_id,

        // Direct specific formatting (ensure type safety and correct field names for backend)
        ...(data.expense_type === 'Direct' && {
           paid_from_account_id: data.paid_from_account_id,
           direct_expense_account_id: data.direct_expense_account_id,
           direct_amount: Number(data.direct_amount), // Ensure it's a number
           // Remove adjustment fields if necessary for backend clarity
           adjustment_expense_account_id: undefined,
           adjustment_credit_account_id: undefined,
           adjustment_amount: undefined,
        }),

        // Adjustment specific formatting
        ...(data.expense_type === 'Adjustment' && {
           paid_from_account_id: data.paid_from_account_id, // Account to CREDIT (money goes out)
           paid_to_account_id: data.paid_to_account_id, // Account to DEBIT (money goes in)
           adjustment_amount: Number(data.adjustment_amount), // Ensure it's a number
           // Clear direct-expense specific fields
           direct_expense_account_id: undefined,
           direct_amount: undefined,
        }),
    };

    console.log("Submitting Formatted Data:", formattedData);
    onSubmit(formattedData);
  };

  // Helper function to handle numeric input change for Zod
  const handleNumericChange = (fieldOnChange: (...event: any[]) => void, value: string) => {
      if (value === '' || value === '-') {
          fieldOnChange(undefined); // Allow clearing the input or typing negative sign initially
      } else {
          const num = parseFloat(value);
          fieldOnChange(isNaN(num) ? undefined : num);
      }
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{isEditMode ? 'Edit Expense' : 'Record Expense'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start"> {/* Use items-start for label alignment */}
              {/* Site Selection */}
              <FormField
                control={form.control}
                name="site_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Site *</FormLabel>
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString()}
                      disabled={isSubmitting}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select a site" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {sites.map((site) => (
                          <SelectItem key={site.id} value={site.id.toString()}>
                            {site.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              {/* Expense Type */}
              <FormField
                control={form.control}
                name="expense_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expense Type</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value}
                      disabled={isSubmitting || isEditMode} // Disable type change in edit mode
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select expense type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Direct">Direct Expense (Paid)</SelectItem>
                        <SelectItem value="Adjustment">Transfer/Adjustment</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Transaction Date */}
              <FormField
                control={form.control}
                name="transaction_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col"> {/* Remove pt-2 if labels are top-aligned */}
                    <FormLabel>Date *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal", // Use justify-start
                              !field.value && "text-muted-foreground"
                            )}
                            disabled={isSubmitting}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 opacity-50" /> {/* Icon on left */}
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Optional Reference (Common Field) - Moved up for better flow */}
               <FormField
                  control={form.control}
                  name="reference_number"
                  render={({ field }) => (
                      <FormItem>
                          <FormLabel>Reference</FormLabel>
                          <FormControl><Input {...field} value={field.value ?? ''} placeholder="e.g., Cheque No, Txn ID" disabled={isSubmitting} /></FormControl>
                          <FormMessage />
                      </FormItem>
                  )}
              />
            </div>

            {/* --- Conditional Fields --- */}

            {/* Fields for Type: Direct */}
            {expenseType === 'Direct' && (
              <div className="space-y-4 pt-4 border-t mt-4">
                 <h4 className="font-medium text-md mb-2">Direct Payment Details</h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                   {/* Paid From Account */}
                     <FormField
                        control={form.control}
                        name="paid_from_account_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Paid From Account *</FormLabel>
                            <SimpleAccountSelector
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select Bank/Cash Account"
                                disabled={isSubmitting}
                                // Filter to show only cash and bank accounts
                                filterAccountIds={expenseAccountIds}
                             />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    {/* Expense Account */}
                    <FormField
                        control={form.control}
                        name="direct_expense_account_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Expense Account *</FormLabel>
                             <SimpleAccountSelector
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select Expense Account"
                                disabled={isSubmitting}
                                // Filter for active Expense accounts [cite: 1]
                                filterAccountTypeIds={[ACCOUNT_TYPES.EXPENSE]}
                                // filterAccountNature="debit" // Expenses are debit nature
                             />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                     {/* Amount */}
                    <FormField
                        control={form.control}
                        name="direct_amount"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Amount *</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    {...field}
                                    value={field.value ?? ''} // Handle undefined for controlled input
                                    onChange={e => handleNumericChange(field.onChange, e.target.value)}
                                    placeholder="0.00"
                                    disabled={isSubmitting}
                                  />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                 </div>
              </div>
            )}

            {/* Fields for Type: Adjustment */}
            {expenseType === 'Adjustment' && (
               <div className="space-y-4 pt-4 border-t mt-4">
                 <h4 className="font-medium text-md mb-2">Transfer/Adjustment Details</h4>
                 <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                    {/* Paid From Account (will be credited) */}
                    <FormField
                        control={form.control}
                        name="paid_from_account_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Paid From *</FormLabel>
                             <SimpleAccountSelector
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select source account"
                                disabled={isSubmitting}
                                // Allow any account type to be credited in adjustment
                             />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                    {/* Paid To Account (will be debited) */}
                    <FormField
                        control={form.control}
                        name="paid_to_account_id"
                        render={({ field }) => (
                        <FormItem>
                            <FormLabel>Paid To *</FormLabel>
                             <SimpleAccountSelector
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select destination account"
                                disabled={isSubmitting}
                                // Allow any account type to be debited in adjustment
                             />
                            <FormMessage />
                        </FormItem>
                        )}
                    />

                     {/* Amount */}
                    <FormField
                        control={form.control}
                        name="adjustment_amount"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Amount *</FormLabel>
                                <FormControl>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    {...field}
                                    value={field.value ?? ''} // Handle undefined
                                    onChange={e => handleNumericChange(field.onChange, e.target.value)}
                                    placeholder="0.00"
                                    disabled={isSubmitting}
                                   />
                                 </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                 </div>
              </div>
            )}

             {/* Narration (Common) */}
             <FormField
                control={form.control}
                name="narration"
                render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl><Textarea {...field} value={field.value ?? ''} placeholder="Enter notes (optional)" rows={3} disabled={isSubmitting} /></FormControl>
                      <FormMessage />
                    </FormItem>
                )}
              />
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || !form.formState.isValid}> {/* Disable if form is invalid */}
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Recording...
              </>
            ) : (
               isEditMode ? 'Update Expense' : 'Record Expense' // Adjust button text for edit mode
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};